import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "../components/Layout";
import api from "../lib/api";
import { CustomerSelect } from "../components/CustomerSelect";
import { ItemSelect } from "../components/ItemSelect";
import { fmtCur } from "../lib/format";
import { Plus, Trash, ArrowLeft, FloppyDisk, Sparkle, UploadSimple, SpinnerGap, X } from "@phosphor-icons/react";

const CURRENCIES = ["USD", "INR", "GBP", "EUR"];
const emptyLine = () => ({ sku: "", description: "", quantity_ordered: "", unit_price: "", uom: "EA", due_date: "", rev: "" });

export default function ManualPO() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [revPrompt, setRevPrompt] = useState(null); // { existing, diff, pushed }
  const fileRef = useRef(null);
  const [extracting, setExtracting] = useState(false);
  const [aiNote, setAiNote] = useState(null); // { filename, count, pct, multiple }
  const [h, setH] = useState({
    customer_name: "", portal_pcode: "", customer_email: "", po_number: "",
    order_date: "", requested_ship_date: "", currency: "USD", notes: "",
    ship_to_address: "", bill_to_address: "",
  });
  const [lines, setLines] = useState([emptyLine()]);

  const setField = (k, v) => setH((p) => ({ ...p, [k]: v }));
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const addLine = () => setLines((ls) => [...ls, emptyLine()]);
  const removeLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));

  const lineTotal = (l) => (Number(l.quantity_ordered) || 0) * (Number(l.unit_price) || 0);
  const poTotal = lines.reduce((s, l) => s + lineTotal(l), 0);

  const onPickFile = () => fileRef.current?.click();
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!f) return;
    setExtracting(true);
    setAiNote(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/po/extract-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!data.found) {
        toast.error(data.message || "Nothing could be read from that file");
        setAiNote({ error: true, filename: data.filename || f.name, message: data.message });
        return;
      }
      const po = data.po || {};
      setH((p) => ({
        ...p,
        // AI name is free text — user must re-pick from the curated list to lock the ERP code,
        // so we DO NOT set portal_pcode here (keeps the "confirm customer" prompt honest).
        customer_name: po.customer_name || p.customer_name,
        portal_pcode: po.customer_name ? "" : p.portal_pcode,
        customer_email: po.customer_email || p.customer_email,
        po_number: po.po_number || p.po_number,
        order_date: po.order_date || p.order_date,
        currency: po.currency || p.currency,
        ship_to_address: po.ship_to_address || p.ship_to_address,
        bill_to_address: po.bill_to_address || p.bill_to_address,
        notes: po.notes || p.notes,
      }));
      const mapped = (po.line_items || []).map((l) => ({
        sku: l.sku || "", description: l.description || "",
        quantity_ordered: l.quantity_ordered ?? "", unit_price: l.unit_price ?? "",
        uom: l.uom || "EA", due_date: l.due_date || "", rev: l.rev || "",
      }));
      setLines(mapped.length ? mapped : [emptyLine()]);
      const pct = po.confidence != null ? Math.round(po.confidence * 100) : null;
      setAiNote({ filename: data.filename, count: data.count, pct, multiple: data.multiple });
      toast.success(`AI read ${f.name} — review the details, then Create PO`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not read that file with AI");
    } finally {
      setExtracting(false);
    }
  };

  const submit = async (decision = null) => {
    if (!h.customer_name.trim()) return toast.error("Select a customer");
    if (!h.po_number.trim()) return toast.error("Enter a PO number");
    const valid = lines.filter((l) => l.sku.trim() || l.description.trim());
    if (valid.length === 0) return toast.error("Add at least one line item");
    setSaving(true);
    try {
      const dueDates = valid.map((l) => l.due_date).filter(Boolean).sort();
      const payload = {
        ...h,
        requested_ship_date: dueDates[0] || null,  // header ship date = earliest line due date
        portal_pcode: h.portal_pcode || null,
        revision_decision: decision,
        line_items: valid.map((l) => ({
          sku: l.sku.trim(), rev: l.rev.trim() || null, description: l.description.trim(),
          quantity_ordered: Number(l.quantity_ordered) || 0,
          unit_price: l.unit_price === "" ? null : Number(l.unit_price),
          uom: l.uom || "EA", due_date: l.due_date || null,
        })),
      };
      const { data } = await api.post("/po/manual", payload);
      // A PO with this number already exists and the content differs → ask the user inline.
      if (data.status === "revision_review_required") {
        setRevPrompt({ existing: data.existing, diff: data.diff || [], pushed: data.pushed });
        setSaving(false);
        return;
      }
      setRevPrompt(null);
      const verb = decision === "new_release" ? "added as a new release"
        : decision === "revision" ? "saved as a revision" : "created";
      toast.success(`PO ${data.po_number || h.po_number} ${verb} — awaiting review`);
      navigate("/intake-review");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create PO");
    } finally { setSaving(false); }
  };

  const fld = { display: "flex", flexDirection: "column" };

  return (
    <Layout breadcrumb={["Home", "PO Intake Review", "New PO"]}>
      <div className="jde-page">
        <div className="jde-panel" data-testid="manual-po-panel">
          <div className="jde-panel-header">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button className="jde-btn jde-btn-sm" onClick={() => navigate("/intake-review")} data-testid="manual-po-back">
                <ArrowLeft size={12} weight="bold" /> Back
              </button>
              <div className="jde-panel-title">NEW PURCHASE ORDER (MANUAL ENTRY)</div>
            </div>
            <button className="jde-btn jde-btn-primary" onClick={() => submit()} disabled={saving || extracting} data-testid="manual-po-save">
              <FloppyDisk size={12} weight="bold" /> {saving ? "Saving…" : "Create PO"}
            </button>
          </div>

          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,image/*,application/pdf"
                 onChange={onFile} style={{ display: "none" }} data-testid="manual-ai-file-input" />

          <div className="jde-panel-body">
            {/* AI upload / auto-fill */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                          padding: "12px 14px", marginBottom: 18, borderRadius: 8,
                          border: "1px dashed var(--jde-border, #cbd5e1)", background: "#F8FAFC" }}>
              <button className="jde-btn" onClick={onPickFile} disabled={extracting || saving}
                      data-testid="manual-ai-upload-btn"
                      style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                {extracting
                  ? <SpinnerGap size={14} weight="bold" className="spin" />
                  : <UploadSimple size={14} weight="bold" />}
                {extracting ? "Reading with AI…" : "Upload PO & auto-fill with AI"}
              </button>
              <div style={{ fontSize: 12.5, color: "var(--omh-text-muted, #64748B)" }}>
                <Sparkle size={13} weight="fill" style={{ color: "#8B5CF6", verticalAlign: "-2px", marginRight: 4 }} />
                Upload a PO PDF or image (scanned is fine). AI fills the form below — you review &amp; confirm before it's created.
              </div>
            </div>

            {aiNote && aiNote.error && (
              <div data-testid="manual-ai-error" style={{
                display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", marginBottom: 16,
                borderRadius: 8, border: "1px solid #FECACA", background: "#FEF2F2", fontSize: 12.5,
              }}>
                <Sparkle size={15} weight="fill" style={{ color: "#DC2626", marginTop: 1, flexShrink: 0 }} />
                <div style={{ flex: 1, color: "#991B1B" }}>
                  Couldn't read a PO from <strong>{aiNote.filename}</strong>.{" "}
                  {aiNote.message || "The file may be too small, blank, or not a purchase order."}{" "}
                  Please enter the PO manually below, or try a clearer scan.
                </div>
                <button className="jde-btn jde-btn-sm" onClick={() => setAiNote(null)} data-testid="manual-ai-error-dismiss"
                        style={{ padding: "2px 6px" }} title="Dismiss">
                  <X size={11} weight="bold" />
                </button>
              </div>
            )}

            {aiNote && !aiNote.error && (
              <div data-testid="manual-ai-note" style={{
                display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", marginBottom: 16,
                borderRadius: 8, border: "1px solid #BFDBFE", background: "#EFF6FF", fontSize: 12.5,
              }}>
                <Sparkle size={15} weight="fill" style={{ color: "#8B5CF6", marginTop: 1, flexShrink: 0 }} />
                <div style={{ flex: 1, color: "#1E3A8A" }}>
                  AI filled this form from <strong>{aiNote.filename}</strong>
                  {aiNote.pct != null && <> · confidence <strong>{aiNote.pct}%</strong></>}.{" "}
                  <strong>Please review every field</strong> — especially confirm the <strong>customer from your list</strong> so it maps to the ERP, then click <strong>Create PO</strong>.
                  {aiNote.multiple && <> This file looked like it had multiple POs; only the first was loaded — add the rest manually or upload them separately.</>}
                </div>
                <button className="jde-btn jde-btn-sm" onClick={() => setAiNote(null)} data-testid="manual-ai-note-dismiss"
                        style={{ padding: "2px 6px" }} title="Dismiss">
                  <X size={11} weight="bold" />
                </button>
              </div>
            )}

            {/* Header fields */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14, marginBottom: 18 }}>
              <div style={{ gridColumn: "span 1" }}>
                <CustomerSelect name={h.customer_name} pcode={h.portal_pcode}
                                onChange={({ name, pcode }) => setH((p) => ({ ...p, customer_name: name, portal_pcode: pcode }))}
                                source="mongo" testid="manual-customer" />
              </div>
              <div style={fld}>
                <label className="jde-label">PO Number</label>
                <input className="jde-input" value={h.po_number} onChange={(e) => setField("po_number", e.target.value)} data-testid="manual-po-number" />
              </div>
              <div style={fld}>
                <label className="jde-label">Customer Email</label>
                <input className="jde-input" value={h.customer_email} onChange={(e) => setField("customer_email", e.target.value)} data-testid="manual-cust-email" />
              </div>
              <div style={fld}>
                <label className="jde-label">PO Date</label>
                <input className="jde-input" type="date" value={h.order_date} onChange={(e) => setField("order_date", e.target.value)} data-testid="manual-po-date" />
              </div>
              <div style={fld}>
                <label className="jde-label">Currency</label>
                <select className="jde-input" value={h.currency} onChange={(e) => setField("currency", e.target.value)} data-testid="manual-currency">
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 18 }}>
              <div style={fld}>
                <label className="jde-label">Ship-To Address</label>
                <textarea className="jde-textarea" rows={2} value={h.ship_to_address} onChange={(e) => setField("ship_to_address", e.target.value)} data-testid="manual-ship-to" />
              </div>
              <div style={fld}>
                <label className="jde-label">Bill-To Address</label>
                <textarea className="jde-textarea" rows={2} value={h.bill_to_address} onChange={(e) => setField("bill_to_address", e.target.value)} data-testid="manual-bill-to" />
              </div>
            </div>

            {/* Line items */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--omh-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Line Items ({lines.length})
              </div>
              <button className="jde-btn jde-btn-sm" onClick={addLine} data-testid="manual-add-line"><Plus size={11} weight="bold" /> Add line</button>
            </div>
            <table className="jde-grid" data-testid="manual-lines" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ minWidth: 130 }}>Part # (SKU)</th>
                  <th>Description</th>
                  <th style={{ width: 90, textAlign: "right" }}>Qty</th>
                  <th style={{ width: 70 }}>UOM</th>
                  <th style={{ width: 110, textAlign: "right" }}>Unit Price</th>
                  <th style={{ width: 140 }}>Due Date</th>
                  <th style={{ width: 60 }}>Rev</th>
                  <th style={{ width: 110, textAlign: "right" }}>Line Total</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} data-testid={`manual-line-${i}`}>
                    <td><ItemSelect value={l.sku}
                                    onSelect={(it) => setLines((ls) => ls.map((row, idx) => idx === i ? { ...row, sku: it.icode, description: it.iname, uom: it.unit || "EA" } : row))}
                                    onText={(t) => setLine(i, "sku", t)}
                                    testid={`manual-line-sku-${i}`} /></td>
                    <td data-testid={`manual-line-desc-${i}`} style={{ color: l.description ? "inherit" : "#94A3B8" }}>{l.description || "— (select a SKU)"}</td>
                    <td><input className="jde-input num" type="number" value={l.quantity_ordered} onChange={(e) => setLine(i, "quantity_ordered", e.target.value)} data-testid={`manual-line-qty-${i}`} /></td>
                    <td><input className="jde-input" value={l.uom} onChange={(e) => setLine(i, "uom", e.target.value)} /></td>
                    <td><input className="jde-input num" type="number" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} data-testid={`manual-line-price-${i}`} /></td>
                    <td><input className="jde-input" type="date" value={l.due_date} onChange={(e) => setLine(i, "due_date", e.target.value)} data-testid={`manual-line-due-${i}`} /></td>
                    <td><input className="jde-input" value={l.rev} onChange={(e) => setLine(i, "rev", e.target.value)} placeholder="—" /></td>
                    <td className="num text-mono">{fmtCur(lineTotal(l), h.currency)}</td>
                    <td style={{ textAlign: "center" }}>
                      <button className="jde-btn jde-btn-sm" onClick={() => removeLine(i)} data-testid={`manual-line-remove-${i}`} title="Remove line"
                              style={{ color: "var(--omh-alert)", borderColor: "var(--omh-alert)", padding: "2px 6px" }}>
                        <Trash size={11} weight="bold" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={7} style={{ textAlign: "right", fontWeight: 700 }}>PO Total</td>
                  <td className="num text-mono" style={{ fontWeight: 700 }} data-testid="manual-po-total">{fmtCur(poTotal, h.currency)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>

            <div style={{ marginTop: 14 }}>
              <label className="jde-label">Notes</label>
              <textarea className="jde-textarea" rows={2} value={h.notes} onChange={(e) => setField("notes", e.target.value)} data-testid="manual-notes" />
            </div>
          </div>
        </div>
      </div>

      {revPrompt && (
        <div data-testid="manual-revision-dialog" style={{
          position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }} onClick={() => !saving && setRevPrompt(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: "#fff", borderRadius: 12, maxWidth: 560, width: "100%", padding: 22,
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          }}>
            <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 4 }}>This PO already exists</div>
            <div className="muted" style={{ fontSize: 13, marginBottom: 14 }}>
              PO <strong>{revPrompt.existing?.po_number || h.po_number}</strong> for{" "}
              <strong>{revPrompt.existing?.customer_name || h.customer_name}</strong> is already on file
              {revPrompt.pushed ? " and has been pushed to the ERP" : ""}. Is this a revision of it, or a new release?
            </div>

            {revPrompt.diff?.length > 0 && (
              <div style={{ border: "1px solid #E2E8F0", borderRadius: 8, padding: 10, marginBottom: 16, background: "#F8FAFC" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#64748B", marginBottom: 6 }}>WHAT CHANGED</div>
                {revPrompt.diff.map((d, i) => (
                  <div key={i} style={{ fontSize: 12.5, marginBottom: 3 }} data-testid={`manual-revision-diff-${i}`}>
                    <span style={{ fontWeight: 600 }}>{d.field}:</span>{" "}
                    <span style={{ color: "#B91C1C" }}>{String(d.old ?? "—")}</span>{" → "}
                    <span style={{ color: "#166534", fontWeight: 600 }}>{String(d.new ?? "—")}</span>
                  </div>
                ))}
              </div>
            )}

            {revPrompt.pushed && (
              <div style={{ fontSize: 12, color: "#92400E", background: "#FEF3C7", padding: "8px 10px", borderRadius: 6, marginBottom: 14 }}>
                Note: this PO is already in the ERP. Choosing “Revision” records the change for review and re-sync — it won’t silently overwrite the ERP.
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button className="jde-btn jde-btn-sm" onClick={() => setRevPrompt(null)} disabled={saving} data-testid="manual-revision-cancel">Cancel</button>
              <button className="jde-btn" onClick={() => submit("new_release")} disabled={saving} data-testid="manual-revision-new-release">
                New release (keep both)
              </button>
              <button className="jde-btn jde-btn-primary" onClick={() => submit("revision")} disabled={saving} data-testid="manual-revision-replace">
                {saving ? "Saving…" : "Revision (replace existing)"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
